import React, { useEffect, useMemo, useState } from "react";
import "./../../../assets/css/admin/gestionSignalement/AdminSignalementsPage.css";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import useSignalement from "../../../hooks/useSignalement";
import LoaderAdminUsers from "../../../components/loader/admin/gestionUtilisateur/LoaderAdminUsers";
import { useNavigate } from "react-router-dom";

export default function AdminSignalementsPage({ setAlert }) {
  const {
    fetchSignalements,
    deleteSignalement,
    updateSignalement,
    validateSignalement,
    rejectSignalement,
  } = useSignalement();

  const [signalements, setSignalements] = useState([]);
  const [loading, setLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Filtres
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all"); // 1: en attente, 2: validé, 3: rejeté
  const [type, setType] = useState("all"); // 1: fraude, 2: contenu inapproprié

  // Date range
  const [dateMin, setDateMin] = useState("");
  const [dateMax, setDateMax] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const navigate = useNavigate();

  const breadcrumbItems = [
    { label: "Mon Compte", link: "/utilisateur/compte" },
    { label: "Signalements" },
  ];

  const normalized = (v) =>
    String(v ?? "")
      .toLowerCase()
      .trim();

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

  const startOfDayTs = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
  };

  const endOfDayTs = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
  };

  const signalementDateTs = (s) => {
    const v = s?.createdAt || s?.date;
    const t = v ? new Date(v).getTime() : NaN;
    return Number.isFinite(t) ? t : null;
  };

  const onChangeDateMin = (nextMin) => {
    setDateMin(nextMin);
    if (nextMin && dateMax && startOfDayTs(dateMax) < startOfDayTs(nextMin)) {
      setDateMax(nextMin);
    }
  };

  const onChangeDateMax = (nextMax) => {
    setDateMax(nextMax);
    if (nextMax && dateMin && startOfDayTs(nextMax) < startOfDayTs(dateMin)) {
      setDateMin(nextMax);
    }
  };

  const clearDates = () => {
    setDateMin("");
    setDateMax("");
  };

  // ✅ Labels / Badges status (Option B)
  const statusLabel = (s) => {
    const v = Number(s?.status);
    if (v === 1) return "En attente";
    if (v === 2) return "Validé";
    if (v === 3) return "Rejeté";
    return "—";
  };

  const statusBadgeClass = (v) => {
    const n = Number(v);
    if (n === 1) return "badgePending";
    if (n === 2) return "badgeValid";
    if (n === 3) return "badgeReject";
    return "badgeSoft";
  };

  const typeLabel = (s) => {
    if (s?.typeSignalement != null) {
      return Number(s?.typeSignalement) === 1
        ? "Fraude"
        : Number(s?.typeSignalement) === 2
          ? "Contenu inapproprié"
          : "Autre";
    }
    return s?.motif || s?.raison || "—";
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetchSignalements({});
      // ✅ Adaptez ici selon votre backend
      // vous aviez: res.data.data
      const list =
        res?.data?.data?.signalements ||
        res?.data?.data ||
        res?.data?.signalements ||
        res?.data ||
        [];
      setSignalements(Array.isArray(list) ? list : []);
    } catch (err) {
      console.log(err);
      setAlert?.({
        type: "danger",
        message: "Impossible de récupérer les signalements",
      });
    } finally {
      setTimeout(() => setLoading(false), 200);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredSignalements = useMemo(() => {
    let list = [...(signalements || [])];

    if (q.trim()) {
      const query = normalized(q);
      list = list.filter((s) => {
        const signaleurName =
          `${s?.idSignaleur?.nom ?? ""} ${s?.idSignaleur?.prenom ?? ""}`.trim();
        const ownerName =
          `${s?.idUtilisateur?.nom ?? ""} ${s?.idUtilisateur?.prenom ?? ""}`.trim();
        const articleTitre = s?.idArticle?.titre ?? "";
        const message = s?.message ?? s?.description ?? "";

        return (
          normalized(signaleurName).includes(query) ||
          normalized(ownerName).includes(query) ||
          normalized(s?.idSignaleur?.email).includes(query) ||
          normalized(s?.idUtilisateur?.email).includes(query) ||
          normalized(articleTitre).includes(query) ||
          normalized(message).includes(query)
        );
      });
    }

    // Status filter
    if (status !== "all")
      list = list.filter((s) => String(s?.status) === String(status));

    // Type filter
    if (type !== "all") {
      list = list.filter((s) => String(s?.typeSignalement) === String(type));
    }

    // Date range
    const minTs = dateMin ? startOfDayTs(dateMin) : null;
    const maxTs = dateMax ? endOfDayTs(dateMax) : null;

    if (minTs !== null || maxTs !== null) {
      list = list.filter((s) => {
        const t = signalementDateTs(s);
        if (t === null) return false;
        if (minTs !== null && t < minTs) return false;
        if (maxTs !== null && t > maxTs) return false;
        return true;
      });
    }

    // Sort newest first
    list.sort(
      (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0),
    );

    return list;
  }, [signalements, q, status, type, dateMin, dateMax]);

  useEffect(() => {
    setPage(1);
  }, [q, status, type, dateMin, dateMax]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredSignalements.length / PAGE_SIZE),
  );
  const safePage = Math.min(page, totalPages);

  const pagedSignalements = filteredSignalements.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const getFullName = (user) => {
    return `${user?.nom ?? ""} ${user?.prenom ?? ""}`.trim() || "—";
  };

  const openDeleteModal = (s) => {
    setDeleteTarget(s);
    setShowDeleteModal(true);
  };

  const userSignalementsCount = (userId) =>
    signalements.filter((s) => s?.idUtilisateur?._id === userId).length;

  const articleSignalementsCount = (articleId) =>
    signalements.filter((s) => s?.idArticle?._id === articleId).length;

  const handleValidate = async (s) => {
    if (!s?._id) return;
    if (Number(s?.status) !== 1) return;

    try {
      const res = await validateSignalement(s._id);
      console.log(res);

      const userDisabled = res?.data?.data?.stats?.userDisabled;

      setAlert?.({
        type: "success",
        message: userDisabled
          ? "Signalement validé. L'utilisateur a été désactivé (seuil atteint)."
          : "Signalement validé.",
      });

      fetchData();
    } catch (e) {
      console.log(e);
      setAlert?.({
        type: "danger",
        message: "Impossible de valider le signalement.",
      });
    }
  };

  const handleReject = async (s) => {
    if (!s?._id) return;
    if (Number(s?.status) !== 1) return;

    try {
      await rejectSignalement(s._id);
      setAlert?.({ type: "success", message: "Signalement rejeté." });
      fetchData();
    } catch (e) {
      console.log(e);
      setAlert?.({
        type: "danger",
        message: "Impossible de rejeter le signalement.",
      });
    }
  };

  return (
    <>
      <div className="adminSignalementPage">
        <div className="container">
          <Breadcrumb items={breadcrumbItems} />

          <div className="adminSignalementGrid">
            <section className="adminSignalementCard adminSignalementCardCenter">
              <div className="adminSignalementHeader">
                <div>
                  <div className="adminSignalementTitle">
                    Gestion des signalements
                  </div>
                  <div className="adminSignalementSubtitle">
                    {loading
                      ? "Chargement..."
                      : `${filteredSignalements.length} signalement(s)`}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button
                    className="adminSignalementBtn adminSignalementBtnSoft"
                    onClick={fetchData}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-clockwise" /> Rafraîchir
                  </button>
                </div>
              </div>

              {/* FILTERS */}
              <div className="adminSignalementFilters">
                <div className="adminSignalementSearch">
                  <i className="bi bi-search" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Rechercher : signaleur, propriétaire, article, message..."
                  />
                </div>

                <div className="adminSignalementSelects">
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="all">Statut</option>
                    <option value="1">En attente</option>
                    <option value="2">Validé</option>
                    <option value="3">Rejeté</option>
                  </select>

                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    <option value="all">Type</option>
                    <option value="1">Fraude</option>
                    <option value="2">Contenu inapproprié</option>
                  </select>
                </div>

                <div className="adminSignalementDateRange">
                  <div className="adminSignalementDateField">
                    <label className="adminSignalementDateLabel">
                      Date min
                    </label>
                    <input
                      type="date"
                      value={dateMin}
                      onChange={(e) => onChangeDateMin(e.target.value)}
                      max={dateMax || undefined}
                    />
                  </div>

                  <div className="adminSignalementDateField">
                    <label className="adminSignalementDateLabel">
                      Date max
                    </label>
                    <input
                      type="date"
                      value={dateMax}
                      onChange={(e) => onChangeDateMax(e.target.value)}
                      min={dateMin || undefined}
                    />
                  </div>

                  {(dateMin || dateMax) && (
                    <button
                      type="button"
                      className="adminSignalementBtn adminSignalementBtnSoft"
                      onClick={clearDates}
                      title="Réinitialiser la période"
                    >
                      <i className="bi bi-x-circle" /> Période
                    </button>
                  )}
                </div>
              </div>

              {loading ? (
                <LoaderAdminUsers />
              ) : pagedSignalements.length === 0 ? (
                <div className="adminSignalementEmpty">
                  <i className="bi bi-flag" /> Aucun signalement
                </div>
              ) : (
                <div className="adminSignalementTableWrap">
                  <table className="adminSignalementTable">
                    <thead>
                      <tr>
                        <th>Signaleur</th>
                        <th>Propriétaire article</th>
                        <th>Article</th>
                        <th className="hideOnMobile">Type</th>
                        <th>Statut</th>
                        <th className="hideOnMobile">Date</th>
                        <th className="colActions">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {pagedSignalements.map((s) => {
                        const articleTitre = s?.idArticle?.titre ?? "—";
                        const msg = s?.message ?? s?.description ?? "";

                        return (
                          <tr key={s._id}>
                            {/* Signaleur */}
                            <td>
                              <div className="adminSignalementCellUser">
                                <div className="adminSignalementCellUserText">
                                  <div className="adminSignalementName">
                                    {getFullName(s?.idSignaleur) || "—"}
                                  </div>
                                  <div className="adminSignalementMobileMeta">
                                    <div>
                                      <i className="bi bi-megaphone" />{" "}
                                      {typeLabel(s)}
                                    </div>
                                    <div>
                                      <i className="bi bi-calendar3" />{" "}
                                      {formatDate(s.createdAt)}
                                    </div>
                                    {msg ? (
                                      <div>
                                        <i className="bi bi-chat-left-text" />{" "}
                                        <span className="adminSignalementMsgClamp">
                                          {msg}
                                        </span>
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Propriétaire */}
                            <td>
                              <div
                                className="adminSignalementCellUser"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/admin/utilisateurs?id=${s?.idUtilisateur?._id}`,
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <div className="adminSignalementCellUserText">
                                  <div className="adminSignalementName">
                                    {getFullName(s?.idUtilisateur) || "—"}
                                  </div>
                                  <div
                                    className={`countBadge ${s?.userSignalementCount >= 3 ? "dangerHigh" : ""}`}
                                  >
                                    {s?.userSignalementCount} signalement(s)
                                    utilisateur
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Article */}
                            <td>
                              <div
                                className="adminSignalementArticleCell"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/admin/articles?id=${s?.idArticle?._id}`,
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <div className="adminSignalementArticleTitle">
                                  {articleTitre}
                                </div>
                                <div
                                  className={`countBadge ${s?.articleSignalementCount >= 3 ? "dangerHigh" : ""}`}
                                >
                                  {s?.articleSignalementCount} signalement(s)
                                  article
                                </div>
                              </div>
                            </td>

                            <td className="hideOnMobile">{typeLabel(s)}</td>

                            {/* Statut */}
                            <td>
                              <span
                                className={`adminSignalementBadge ${statusBadgeClass(s.status)}`}
                              >
                                {statusLabel(s)}
                              </span>
                            </td>

                            <td className="hideOnMobile">
                              {formatDate(s.createdAt)}
                            </td>

                            {/* Actions */}
                            <td className="colActions">
                              <div className="adminSignalementActions">
                                {/* ✅ Valider */}
                                <button
                                  type="button"
                                  className="adminSignalementIconBtn"
                                  title="Valider"
                                  disabled={Number(s?.status) !== 1}
                                  onClick={() => handleValidate(s)}
                                >
                                  <i className="bi bi-check2-circle" />
                                </button>

                                {/* ✅ Rejeter */}
                                <button
                                  type="button"
                                  className="adminSignalementIconBtn"
                                  title="Rejeter"
                                  disabled={Number(s?.status) !== 1}
                                  onClick={() => handleReject(s)}
                                >
                                  <i className="bi bi-x-circle" />
                                </button>

                                {/* Voir recap */}
                                <button
                                  type="button"
                                  className="adminSignalementIconBtn"
                                  title="Voir le récapitulatif"
                                  onClick={() =>
                                    navigate(
                                      `/admin/signalements/recap?id=${s._id}`,
                                    )
                                  }
                                >
                                  <i className="bi bi-eye" />
                                </button>

                                {/* Archiver (suppression logique) */}
                                <button
                                  type="button"
                                  className="adminSignalementIconBtn"
                                  onClick={() => openDeleteModal(s)}
                                  title="Archiver"
                                >
                                  <i className="bi bi-trash" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* PAGINATION */}
              {!loading && filteredSignalements.length > 0 && (
                <div className="adminSignalementPagination">
                  <button
                    type="button"
                    className="adminSignalementIconBtn"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage <= 1}
                    title="Page précédente"
                  >
                    <i className="bi bi-chevron-left" />
                  </button>

                  <div
                    className="adminSignalementSubtitle"
                    style={{ marginTop: 0 }}
                  >
                    Page <b>{safePage}</b> / {totalPages}
                  </div>

                  <button
                    type="button"
                    className="adminSignalementIconBtn"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages}
                    title="Page suivante"
                  >
                    <i className="bi bi-chevron-right" />
                  </button>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* MODAL ARCHIVER */}
      {showDeleteModal && deleteTarget && (
        <div className="modal fade show d-block">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ zIndex: "9999" }}>
              <div className="modal-header">
                <h5 className="modal-title">⚠️ Archivage du signalement</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowDeleteModal(false)}
                />
              </div>

              <div className="modal-body">
                <p>Voulez-vous vraiment archiver ce signalement ?</p>

                <ul>
                  <li>
                    <b>Signalements de cet utilisateur :</b>{" "}
                    {userSignalementsCount(deleteTarget.idUtilisateur?._id)}
                  </li>
                  <li>
                    <b>Signalements sur cet article :</b>{" "}
                    {articleSignalementsCount(deleteTarget.idArticle?._id)}
                  </li>
                </ul>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Annuler
                </button>

                <button
                  className="btn btn-danger"
                  onClick={async () => {
                    try {
                      await updateSignalement(deleteTarget._id, { archive: 1 });
                      setAlert?.({
                        type: "success",
                        message: "Signalement archivé.",
                      });
                      fetchData();
                      setShowDeleteModal(false);
                    } catch (e) {
                      console.log(e);
                      setAlert?.({
                        type: "danger",
                        message: "Impossible d'archiver le signalement.",
                      });
                    }
                  }}
                >
                  Archiver
                </button>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" />
        </div>
      )}
    </>
  );
}
